/**
 * useContactFilters Hook
 * Responsabilidade: busca global e filtro por completude (chips).
 */

import { useState, useMemo } from 'react';
import { Contact } from '../../../types';
import { isUsableContactName } from '../../../utils/phoneUtils';

export type ContactCompletenessChip = 'all' | 'complete' | 'incomplete';

export interface ContactFilters {
    search: string;
}

export type ContactChipCounts = Record<ContactCompletenessChip, number>;

const initialFilters: ContactFilters = {
    search: '',
};

function contactPhone(contact: Contact): string {
    return contact.whatsapp || contact.phone || '';
}

/** Contato completo = nome utilizável (mesma regra da tabela / formulário). */
export function isContactComplete(contact: Contact): boolean {
    return isUsableContactName(contact.name, contactPhone(contact));
}

function applySearchFilter(contacts: Contact[], search: string): Contact[] {
    if (!search) return contacts;

    const searchLower = search.toLowerCase();
    return contacts.filter((contact) => {
        const name = contact.name ?? '';
        return (
            name.toLowerCase().includes(searchLower) ||
            contact.email?.toLowerCase().includes(searchLower) ||
            contact.whatsapp?.includes(search) ||
            contact.phone?.includes(search)
        );
    });
}

function applyCompletenessChip(
    contacts: Contact[],
    chip: ContactCompletenessChip,
): Contact[] {
    if (chip === 'complete') return contacts.filter(isContactComplete);
    if (chip === 'incomplete') return contacts.filter((c) => !isContactComplete(c));
    return contacts;
}

export const useContactFilters = (
    contacts: Contact[],
    completenessChip: ContactCompletenessChip,
) => {
    const [filters, setFilters] = useState<ContactFilters>(initialFilters);

    const baseFiltered = useMemo(
        () => applySearchFilter(contacts, filters.search),
        [contacts, filters.search],
    );

    const chipCounts = useMemo<ContactChipCounts>(() => {
        let complete = 0;
        let incomplete = 0;
        for (const contact of baseFiltered) {
            if (isContactComplete(contact)) complete += 1;
            else incomplete += 1;
        }
        return {
            all: baseFiltered.length,
            complete,
            incomplete,
        };
    }, [baseFiltered]);

    const filteredContacts = useMemo(
        () => applyCompletenessChip(baseFiltered, completenessChip),
        [baseFiltered, completenessChip],
    );

    const updateSearch = (search: string) => {
        setFilters({ search });
    };

    return {
        filters,
        filteredContacts,
        chipCounts,
        totalCount: contacts.length,
        updateSearch,
    };
};
